import { ActionButton } from '@components/button/types'
import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import { deleteRecognition, Recognition } from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import { hasPermission, RecognitionPermission } from '@shared/permissions'
import { useMutation } from '@tanstack/react-query'
import useMobileRecognitionDrawer, {
  MobileRecognitionDrawerView,
} from '@tenant/recognition/MobileRecognition/useMobileRecognitionDrawer'
import {
  ALL_RECOGNITION_QK,
  RECOGNITION_COUNT_QUERY_KEY,
} from '@tenant/recognition/recognitionQueryKeys'
import { useT } from '@transifex/react'

export const useDraftAndScheduleActions = (recognition: Recognition) => {
  const t = useT()
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()
  const invalidateQuery = useInvalidateQuery()
  const { setView } = useMobileRecognitionDrawer()
  const canEditRecognition = hasPermission(recognition, RecognitionPermission.Edit)
  const canDeleteRecognition = hasPermission(recognition, RecognitionPermission.Delete)

  const deleteRecognitionConfirmDialog = `DeleteRecognitionConfirmDialog-${recognition.id}`

  const { openDialog: openDeleteRecognition, closeDialog: closeDeleteRecognition } =
    useDialog<string>(deleteRecognitionConfirmDialog)

  const { mutateAsync: deleteRecognitionMutation, isPending: isDeletePending } = useMutation({
    mutationFn: deleteRecognition,
    onSuccess: async () => {
      await invalidateQuery([ALL_RECOGNITION_QK])
      await invalidateQuery(RECOGNITION_COUNT_QUERY_KEY)
      showSuccess(t('Recognition deleted successfully'))
    },
    onError: error => handleApiError(error),
  })

  const handleConfirm = async () => {
    if (!!recognition.id && !isDeletePending) {
      await deleteRecognitionMutation(recognition.id)
      closeDeleteRecognition()
    }
  }

  const actions: ActionButton[] = [
    {
      key: `btnEdit-${recognition.id}`,
      visible: canEditRecognition,
      onClick: () => setView(MobileRecognitionDrawerView.RECIPIENTS, { recognition }),
      name: t('Edit'),
      variant: 'primary',
    },
    {
      key: `btnDelete-${recognition.id}`,
      visible: canDeleteRecognition,
      onClick: () => openDeleteRecognition(recognition.id),
      name: t('Delete'),
      variant: 'primary',
      color: colors.redPrimary,
    },
  ]

  return {
    actions,
    deleteRecognitionConfirmDialog,
    handleConfirm,
  }
}

export default useDraftAndScheduleActions
